import { useCallback, memo, ReactNode } from 'react';

import { MonthView } from 'react-calendar';
import { eachMonthOfInterval, max, min, subMonths, addMonths } from 'date-fns';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import { useCalendar } from '../../../../services/hooks';
import { Tooltip } from '../../../../Tooltip';
import { DropdownButton } from '../../../../DropdownButton';
import { MenuItem } from '../../../../MenuItem';
import { Typography } from '../../../../Typography';
import { Link } from '../../../../Link';
import { Box } from '../../../../Box';
import { Icon } from '../../../../Icon';
import { useIsDesktop } from '../../../../hooks';

import useStyles from './Month.styles';

type Value = [Date, Date];

/**
 * Свойства компонента {@link Month}
 */
interface MonthProps {
  /**
   * Выбранный год для отображения в календаре.
   */
  year: number;

  /**
   * Выбранный месяц для отображения в календаре (0-11).
   */
  month: number;

  /**
   * Массив выбранных диапазонов дат.
   */
  value?: Value[];

  /**
   * Callback-функция для обработки изменения даты.
   */
  onChange: (date: Date) => void;

  /**
   * Минимально выбираемая дата.
   */
  minDate: Date;

  /**
   * Максимально выбираемая дата.
   */
  maxDate: Date;

  /**
   * Дополнительная функция для создания текста всплывающей подсказки для заданной даты.
   */
  createTooltipText?: (date: Date) => ReactNode;

  /**
   * Дополнительная функция для определения, должна ли дата быть отключена.
   */
  shouldDisableDate?: (date: Date, rangeStart: Date | null) => boolean;

  /**
   * Callback-функция для обработки удаления даты.
   */
  onDelete: (date: Date) => void;

  /**
   * Дополнительная callback-функция для обработки клика по диапазону.
   */
  onRangeClick?: (value: Value) => void;

  /**
   * Массив праздничных дат.
   */
  holidays?: Date[];

  /**
   * Массив региональных праздничных дат (отображаются с красной точкой).
   */
  regionalHolidays?: Date[];

  /**
   * Текущая дата, на которую наведён курсор.
   */
  hoverDate: Date | null;

  /**
   * Функция для установки даты, на которую наведён курсор.
   */
  setHoverDate: (date: Date | null) => void;

  /**
   * Текущий диапазон дат, или массив из двух null.
   */
  range: Value | [null, null];

  /**
   * Если true, компонент находится в режиме только для чтения.
   */
  readonly?: boolean;

  /**
   * Если true, компонент отключён.
   */
  disabled?: boolean;

  /**
   * Если true, компонент позволяет очистку выбранных дат.
   */
  clearable?: boolean;

  /**
   * Дополнительная callback-функция для обработки очистки выбранных дат.
   */
  onClear?: () => void;

  /**
   * Массив удалённых диапазонов дат.
   */
  deletedRanges?: Value[];

  /**
   * Массив диапазонов дат с признаком денежной компенсации.
   */
  compensationRanges?: Value[];

  /**
   * Массив заблокированных диапазонов дат (проведённые отсутствия) недоступны для выбора.
   */
  blockedRanges?: Value[];

  /**
   * ID для тестирования месяца.
   */
  testId?: string;
}

/**
 * Компонент, закэшированный с помощью React.memo, для отображения месяца в календаре.
 *
 * @internal
 * @param year - Выбранный год для отображения в календаре.
 * @param month - Выбранный месяц для отображения в календаре (0-11).
 * @param value - Массив выбранных диапазонов дат.
 * @param onChange - Callback-функция для обработки изменения даты.
 * @param minDate - Минимально выбираемая дата.
 * @param maxDate - Максимально выбираемая дата.
 * @param createTooltipText - Дополнительная функция для создания текста всплывающей подсказки для заданной даты.
 * @param shouldDisableDate - Дополнительная функция для определения, должна ли дата быть отключена.
 * @param onDelete - Callback-функция для обработки удаления даты.
 * @param onRangeClick - Дополнительная callback-функция для обработки клика по диапазону.
 * @param holidays - Массив праздничных дат.
 * @param hoverDate - Текущая дата, на которую наведён курсор.
 * @param setHoverDate - Функция для установки даты, на которую наведён курсор.
 * @param range - Текущий диапазон дат, или массив из двух null.
 * @param readonly - Если true, компонент находится в режиме только для чтения.
 * @param disabled - Если true, компонент отключён.
 * @param clearable - Если true, компонент позволяет очистку выбранных дат.
 * @param onClear - Дополнительная callback-функция для обработки очистки выбранных дат.
 * @param deletedRanges - Массив удалённых диапазонов дат.
 * @param blockedRanges - Массив заблокированных диапазонов дат (проведённые отсутствия).
 * @param compensationRanges - Массив диапазонов дат с признаком денежной компенсации.
 * @param testId - ID для тестирования месяца.
 * @returns Отрисованный компонент Month.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link MonthProps}
 */
export const Month = memo(
  ({
    year,
    month,
    value,
    onChange,
    minDate,
    maxDate,
    createTooltipText,
    shouldDisableDate,
    onDelete,
    holidays,
    regionalHolidays,
    hoverDate,
    setHoverDate,
    range,
    readonly,
    disabled,
    onRangeClick,
    clearable,
    onClear,
    deletedRanges,
    compensationRanges,
    blockedRanges,
    testId,
  }: MonthProps) => {
    const { classes } = useStyles();
    const calendar = useCalendar();
    const isDesktop = useIsDesktop();

    const activeStartDate = new Date(year, month);
    const monthName = activeStartDate.toLocaleString('ru-RU', {
      month: 'long',
    });
    const formattedMonthName = monthName.replace(
      monthName[0],
      monthName[0].toUpperCase(),
    );

    const handleClear = useCallback(() => {
      if (onClear) {
        onClear();
      }
    }, [onClear]);

    const createTileContent = useCallback(
      ({ date }: { date: Date }) => {
        const clickedRange = value?.find(
          (selectedRange) =>
            date >= selectedRange[0] && date <= selectedRange[1],
        );

        const handleRangeClick = () => {
          if (onRangeClick && clickedRange) {
            onRangeClick(clickedRange);
          }
        };

        const onDateClick = () => {
          if (!range[0]) {
            setHoverDate(date);
          }
        };

        const onMouseEnter = () => {
          if (range[0]) {
            setHoverDate(date);
          }
        };

        const onMouseLeave = () => {
          if (
            date &&
            !range[0] &&
            hoverDate &&
            calendar.isSameDay(date, hoverDate)
          ) {
            setHoverDate(null);
          }
        };

        const tooltipText = createTooltipText ? createTooltipText(date) : '';
        const dayTestId = `year-calendar-input-day-${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

        if (!!clickedRange && onRangeClick) {
          return (
            <Tooltip
              placement="top"
              title={tooltipText}
              disabled={!tooltipText}
            >
              <span>
                <DropdownButton
                  variant="quaternary"
                  verticalPosition="bottom"
                  horizontalPosition="right"
                  button={({ onClick }) => (
                    <div
                      data-testid={dayTestId}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 32,
                        height: 32,
                      }}
                      onClick={(e) => {
                        onClick(e);
                        handleRangeClick();
                      }}
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}
                    />
                  )}
                  menuItems={[
                    <MenuItem key="1" onClick={() => onDelete(date)}>
                      {isDesktop ? (
                        'Удалить диапазон'
                      ) : (
                        <Box display="flex" alignItems="center" gap="8">
                          <Icon
                            name="delete_outline"
                            size="16"
                            color="text.light.primary"
                          />
                          <Box>Удалить диапазон</Box>
                        </Box>
                      )}
                    </MenuItem>,
                  ]}
                />
              </span>
            </Tooltip>
          );
        }

        return (
          <Tooltip placement="top" title={tooltipText} disabled={!tooltipText}>
            <div
              data-testid={dayTestId}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 32,
                height: 32,
              }}
              onClick={onDateClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            />
          </Tooltip>
        );
      },
      [
        isDesktop,
        calendar,
        createTooltipText,
        hoverDate,
        range,
        onRangeClick,
        setHoverDate,
        onDelete,
        value,
      ],
    );

    const createTileClassName = useCallback(
      // eslint-disable-next-line complexity
      ({ date }: { date: Date }) => {
        let classname = `${classes.day}`;

        const isInRange =
          !!date &&
          !!range[0] &&
          !!hoverDate &&
          ((date >= range[0] && date <= hoverDate) ||
            (date >= hoverDate && date <= range[0]));
        const isRangeStart =
          isInRange &&
          date &&
          range[0] &&
          hoverDate &&
          ((calendar.isSameDay(date, range[0]) && hoverDate >= range[0]) ||
            (calendar.isSameDay(date, hoverDate) && hoverDate < range[0]));

        if (isInRange) {
          classname = `${classname} ${classes.selectedRangeDay}`;
        }

        if (isRangeStart) {
          classname = `${classname} ${classes.rangeStart}`;
        }

        if (!(shouldDisableDate ? shouldDisableDate(date, range[0]) : false)) {
          const isRangeEnd =
            isInRange &&
            date &&
            range[0] &&
            hoverDate &&
            ((calendar.isSameDay(date, hoverDate) && range[0] <= hoverDate) ||
              (calendar.isSameDay(date, range[0]) && range[0] > hoverDate));

          if (isRangeEnd) {
            classname = `${classname} ${classes.rangeEnd}`;
          }

          value?.forEach((selectedRange) => {
            if (
              !isInRange &&
              date >= selectedRange[0] &&
              date <= selectedRange[1]
            ) {
              classname = `${classname} ${classes.selectedRangeDay}`;

              if (calendar.isSameDay(date, selectedRange[0])) {
                classname = `${classname} ${classes.selectedRangeDayStart}`;
              }

              if (calendar.isSameDay(date, selectedRange[1])) {
                classname = `${classname} ${classes.selectedRangeDayEnd}`;
              }
            }
          });
        }

        holidays?.forEach((holiday) => {
          if (calendar.isSameDay(date, holiday)) {
            classname = `${classname} ${classes.holiday}`;
          }
        });

        regionalHolidays?.forEach((regionalHoliday) => {
          if (calendar.isSameDay(date, regionalHoliday)) {
            classname = `${classname} ${classes.regionalHoliday}`;
          }
        });

        deletedRanges?.forEach((deleted) => {
          if (date >= deleted[0] && date <= deleted[1] && !range[0]) {
            classname = `${classname} ${classes.deletedRangeDay}`;

            if (calendar.isSameDay(date, deleted[0]) && !range[0]) {
              classname = `${classname} ${classes.deletedRangeDayStart}`;
            }

            if (calendar.isSameDay(date, deleted[1]) && !range[0]) {
              classname = `${classname} ${classes.deletedRangeDayEnd}`;
            }
          }
        });

        compensationRanges?.forEach((compensationRange) => {
          if (date >= compensationRange[0] && date <= compensationRange[1]) {
            classname = `${classname} ${classes.compensationRangeDay}`;

            if (calendar.isSameDay(date, compensationRange[0])) {
              classname = `${classname} ${classes.compensationRangeDayStart}`;
            }

            if (calendar.isSameDay(date, compensationRange[1])) {
              classname = `${classname} ${classes.compensationRangeDayEnd}`;
            }
          }
        });

        blockedRanges?.forEach((blocked) => {
          if (date >= blocked[0] && date <= blocked[1]) {
            classname = `${classname} ${classes.blockedRangeDay}`;

            if (calendar.isSameDay(date, blocked[0])) {
              classname = `${classname} ${classes.blockedRangeDayStart}`;
            }

            if (calendar.isSameDay(date, blocked[1])) {
              classname = `${classname} ${classes.blockedRangeDayEnd}`;
            }
          }
        });

        if (readonly) {
          classname = `${classname} ${classes.readOnly}`;
        }

        return classname;
      },
      [
        calendar,
        classes.day,
        classes.holiday,
        classes.regionalHoliday,
        classes.rangeEnd,
        classes.rangeStart,
        classes.selectedRangeDay,
        classes.selectedRangeDayEnd,
        classes.selectedRangeDayStart,
        classes.readOnly,
        classes.deletedRangeDay,
        classes.deletedRangeDayStart,
        classes.deletedRangeDayEnd,
        classes.compensationRangeDay,
        classes.compensationRangeDayStart,
        classes.compensationRangeDayEnd,
        classes.blockedRangeDay,
        classes.blockedRangeDayStart,
        classes.blockedRangeDayEnd,
        holidays,
        regionalHolidays,
        hoverDate,
        range,
        shouldDisableDate,
        value,
        readonly,
        deletedRanges,
        compensationRanges,
        blockedRanges,
      ],
    );

    return (
      <div data-testid={testId}>
        <div className={classes.monthName}>
          <Typography variant="subtitle2">{formattedMonthName}</Typography>
          {clearable && (
            <Link stroke={false} onClick={handleClear}>
              Сбросить
            </Link>
          )}
        </div>
        <div className={classes.root}>
          <MonthView
            showFixedNumberOfWeeks
            showNeighboringMonth
            valueType="day"
            activeStartDate={activeStartDate}
            value={range}
            locale="ru"
            onClick={onChange}
            minDate={minDate}
            maxDate={maxDate}
            tileClassName={createTileClassName}
            tileContent={createTileContent}
            tileDisabled={({ date }) => {
              const isBlocked =
                blockedRanges?.some(
                  (blocked) => date >= blocked[0] && date <= blocked[1],
                ) && !(value && calendar.isWithinIntervals(date, value));

              if (isBlocked) {
                return !!range[0];
              }

              if (disabled) {
                return value ? !calendar.isWithinIntervals(date, value) : true;
              }

              return shouldDisableDate
                ? shouldDisableDate(date, range[0])
                : false;
            }}
          />
        </div>
      </div>
    );
  },
  (prev, next) => {
    return (
      isEqual(
        omit(prev, 'hoverDate', 'range'),
        omit(next, 'hoverDate', 'range'),
      ) &&
      !(
        !prev.hoverDate ||
        !prev.range[0] ||
        eachMonthOfInterval({
          start: subMonths(
            min(
              [
                prev.hoverDate,
                next.hoverDate,
                prev.range[0],
                next.range[0],
              ].filter((date: Date | null): date is Date => !!date),
            ),
            1,
          ),
          end: addMonths(
            max(
              [
                prev.hoverDate,
                next.hoverDate,
                prev.range[0],
                next.range[0],
              ].filter((date: Date | null): date is Date => !!date),
            ),
            1,
          ),
        })
          .map((date) => date.getMonth())
          .includes(next.month)
      )
    );
  },
);
